import React from 'react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { Dialog, DialogContent, Box } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DialogTransition from './DialogTransition';
import { styled } from '@mui/material/styles';
import FormDialogTitle from '../form/FormDialogTitle';
import ConfirmationDialog from '../ConfirmationDialog';
import * as uiSlice from '../../data/ui.slice';
import HomeFooterBar from './HomeFooterBar';

const PREFIX = 'GenericForm';

export const formClasses = {
  form: `${PREFIX}-form`,
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  subtitle: `${PREFIX}-subtitle`,
  disclaimer: `${PREFIX}-disclaimer`,
  copyButtons: `${PREFIX}-copyButtons`,
  copy: `${PREFIX}-copy`,
};

const StyledForm = styled('form')(({ theme }) => ({
  [`& .${formClasses.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
  },
  [`& .${formClasses.card}`]: {
    width: 500,
    margin: theme.spacing(1),
  },
  [`& .${formClasses.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${formClasses.subtitle}`]: {
    marginTop: 20,
  },
  [`& .${formClasses.disclaimer}`]: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
  },
  [`& .${formClasses.copyButtons}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
  },
  [`& .${formClasses.copy}`]: {
    marginTop: theme.spacing(1),
  },
}));

interface ElementDialogProps {
  title: string;
  faqContext: string;
  open: boolean;
  saving: boolean;
  saved: boolean;
  savingError: string | null;
  save: () => void;
  reset: () => void;
  close: (saved: boolean) => void;
  formState: FormState<any>;
  children: React.ReactNode;
  fullScreen?: boolean | undefined;
}

export default function ElementDialog({
  title,
  faqContext,
  open,
  saving,
  saved,
  savingError,
  save,
  reset,
  close,
  formState,
  children,
  fullScreen = true,
}: ElementDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string | null>(
    null,
  );
  const [lastSubmitCount, setLastSubmitCount] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { isDirty } = formState;

  React.useEffect(() => {
    if (open) {
      dispatch(uiSlice.pushFAQContext(faqContext));
    } else {
      dispatch(uiSlice.popFAQContext());
    }
  }, [open]);

  React.useEffect(() => {
    if (formState.isSubmitting) {
      setSnackbarMessage(null);
    } else if (
      !formState.isSubmitting &&
      !formState.isSubmitSuccessful &&
      formState.isSubmitted &&
      formState.submitCount !== lastSubmitCount
    ) {
      setSnackbarMessage(t('common.formErrors'));
      setSnackbarOpen(true);
      setLastSubmitCount(formState.submitCount);
    }
  }, [formState, lastSubmitCount, t]);

  React.useEffect(() => {
    if (!saving && savingError) {
      setSnackbarOpen(true);
    } else if (!saving && saved) {
      close(true);
    }
  }, [saving, saved, savingError, close]);

  const cancelVehicleDialog = () => {
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      setLastSubmitCount(0);
      reset();
      close(false);
    }
  };

  const confirmCloseDialog = () => {
    setLastSubmitCount(0);
    setConfirmOpen(false);
    reset();
    close(false);
  };

  const cancelConfirmCloseDialog = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={cancelVehicleDialog}
        TransitionComponent={DialogTransition}>
        <ConfirmationDialog
          title={t('common.confirmCloseForm')}
          message={t('common.possibleDataLoss')}
          open={confirmOpen}
          positiveAction={confirmCloseDialog}
          negativeAction={cancelConfirmCloseDialog}
        />
        <FormDialogTitle
          title={title}
          snackbarOpen={snackbarOpen}
          setSnackbarOpen={setSnackbarOpen}
          onCancel={cancelVehicleDialog}
          onSave={save}
          saving={saving}
          savingError={snackbarMessage || savingError}
        />
        <DialogContent>
          <StyledForm>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <Box className={formClasses.form}>{children}</Box>
            </LocalizationProvider>
          </StyledForm>
        </DialogContent>
      </Dialog>
    </>
  );
}
